import { Tooltip, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { ENTITLEMENTS } from '../../application-core/constants';
import KebabMenu from '../KebabMenu';
import { globalStyles } from '../style/gstheme';
import { checkResourceEntitlements } from '../utils/utils';
import withGS from '../withGShoc';
import RenameTemplateDialog from './RenameTemplateDialog';

const styles = () => {
    return Object.assign({
        templateTitle: {
            color: '#13294B',
            paddingTop: 4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        thumbnail: {
            boxShadow: '0px 1px 3px #00000029',
            width: 302,
            height: 170,
            borderRadius: 4,
            padding: 6,
            marginBottom: 12,
            '&:hover': {
                boxShadow: '0px 12px 17px #00000029'
            }
        },
        thumbnailImage: {
            height: 140,
            '&:hover': {
                cursor: 'pointer'
            }
        },
        thumbnailHeading: {
            marginLeft: 12,
            display: 'flex'
        },
        headerText: {
            fontSize: 10,
            fill: '#000000',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        }
    }, globalStyles);
};

const labels = {
    "https://grafsync.graphifi.com/schema/tsv/IRI": "IRI",
    "https://grafsync.graphifi.com/schema/tsv/hierarchy": "Hierarchy",
    "http://www.w3.org/2004/02/skos/core#prefLabel": "Pref Label",
    "http://www.w3.org/2004/02/skos/core#altLabel": "Alt Label",
    "http://www.w3.org/2004/02/skos/core#hiddenLabel": "Hidden Label",
    "http://www.w3.org/2004/02/skos/core#definition": "Definition",
    "http://www.w3.org/2004/02/skos/core#notation": "Notation",
    "http://www.w3.org/2004/02/skos/core#historyNote": "History Note",
    "http://www.w3.org/2004/02/skos/core#editorialNote": "Editorial Note",
    "http://www.w3.org/2004/02/skos/core#changeNote": "Change Note",
    "http://www.w3.org/2004/02/skos/core#scopeNote": "Scope Note",
    "http://www.w3.org/2004/02/skos/core#example": "Example",
};

class TemplateBox extends React.Component {

    constructor(props) {
        super(props);
        const svgData = this.convertConfigToSvg();
        this.state = {
            renameOpen: false,
            svgData
        };
    }

    convertConfigToSvg() {
        const data = this.props.data;
        const classes = this.props.classes;
        const width = 300;
        const height = 200;
        const columns = data.tsvColumnProperty ? data.tsvColumnProperty.length : 1;
        const columnWidth = width / columns;
        const includeHeaderRow = data.tsvTemplateOption ? data.tsvTemplateOption.includes("https://grafsync.graphifi.com/schema/tsv/includeheader") : false;
        let contents = <g fill="#000000" data-test={"with-header-" + includeHeaderRow}>
            {
                data.tsvColumnProperty && data.tsvColumnProperty.map((cp, index) => <g key={index}>
                    <rect fill={includeHeaderRow ? "#97d700" : "#ffffff"} fillOpacity="30%" x={index * columnWidth} y={0} width={columnWidth - 2} height="30" rx="3" />
                    <foreignObject x={index * columnWidth + 6} y={8} width={columnWidth - 2} height="30">
                        <div className={classes.headerText} style={{ width: columnWidth - 8 }}>{labels[cp] || "Column"}</div>
                    </foreignObject>
                    {data.tsvColumnLanguage && data.tsvColumnLanguage[index] !== "und" &&
                        <g>
                            <rect fill="#01579B" x={index * columnWidth + columnWidth - (data.tsvColumnLanguage[index].length > 2 ? 58 : 26)} y={20} width="20" height="12" rx="6" />
                            <text fill="#ffffff" x={index * columnWidth + columnWidth - (data.tsvColumnLanguage[index].length > 2 ? 54 : 22)} y={29} style={{ fontSize: 9.5, letterSpacing: 1 }}>{data.tsvColumnLanguage[index]}</text>
                        </g>
                    }
                </g>)
            }
            {
                data.tsvColumnProperty && data.tsvColumnProperty.map((cp, index) => <g key={index}>
                    <g key={index}>
                        {
                            data.tsvColumnProperty.map((cpInner, innerIndex) =>
                                <rect
                                    key={innerIndex}
                                    fill="#a0a0a0"
                                    fillOpacity="15%"
                                    x={index * columnWidth}
                                    y={(100 / data.tsvColumnProperty.length * innerIndex) + 36}
                                    width={columnWidth - 2}
                                    height={data.tsvColumnProperty.length < 25 ?
                                        (100 / data.tsvColumnProperty.length) - 4
                                        :
                                        4
                                    }
                                    rx="3" />
                            )
                        }
                    </g>
                </g>)
            }
        </g>;

        let svg = <svg xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${width} ${height} `}>
            {contents}
        </svg>;

        return svg;
    }

    rename = () => {
        this.setState({ renameOpen: true });
    };

    changeName = (name) => {
        this.setState({ renameOpen: false }, () => {
            this.props.onUpdate("title", name, this.props.defaultLang);
        });
    };

    exitRename = () => this.setState({ renameOpen: false });

    render() {

        const { classes, data, defaultLang, onSelect, onDelete } = this.props;
        const { svgData, renameOpen } = this.state;

        if (data.pendingDeletion) return null;
        const isReadOnly = checkResourceEntitlements(data, ENTITLEMENTS.CREATE_TSV_TEMPLATE);

        return (
            <React.Fragment>
                <div className={classes.thumbnail}>
                    <div onClick={onSelect} className={classes.thumbnailImage}>
                        {svgData}
                    </div>
                    <div className={classes.thumbnailHeading}>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            <Tooltip title={data.title[defaultLang]}>
                                <Typography data-test="template-title" variant="body2" className={classes.templateTitle}>{data.title[defaultLang]}</Typography>
                            </Tooltip>
                        </div>
                        {!isReadOnly && <div style={{ flex: '0 0', alignSelf: 'right' }}>
                            <KebabMenu
                                items={["Rename", "Delete"]}
                                onSelect={(index) => index === 0 ? this.rename() : onDelete(data.id)}
                            />
                        </div>}
                    </div>
                </div>
                {renameOpen && <RenameTemplateDialog
                    defaultLang={defaultLang}
                    title="Rename Template"
                    text={data.title[defaultLang]}
                    onExitName={this.exitRename}
                    onName={this.changeName} />}
            </React.Fragment>
        );
    }
}

TemplateBox.propTypes = {
    onSelect: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    defaultLang: PropTypes.string.isRequired,
    classes: PropTypes.object.isRequired,
    data: PropTypes.object.isRequired
};

export default withStyles(styles)(withGS(TemplateBox));